import cn from "classnames";
import React, { useMemo, useState } from "react";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import TooltipGlobal from "../../../components/TooltipGlobal";
import styles from "./Overview.module.sass";
// import Tooltip from "../../../components/Tooltip";
import Modal from "../../../components/Modal";
import { useOpenFarmsAverageAPY } from "../../../customHooks/useOpenFarmsAverageAPY";
import { _no, displayWithSkeleton, formatMoney } from "../../../utils";
import { useFarmingContext } from "../context";
import Success from "./Success";
// import Tooltip from "../../../components/Tooltip";
import { useLocation } from "react-router-dom";
import useRWAOverview from "../../../customHooks/useRWAOverview";
import useConfig from "../../../customHooks/useConfig";
import { useSelector } from "react-redux";
import AnimatedNumbers from "../../../components/AnimatedNumbers";

const Overview = ({ className, loading, isEmpty, data }) => {
    const location = useLocation();
    const [visibleModal, setVisibleModal] = useState(false);
    const farmingContext = useFarmingContext();
    
    const page = location.pathname;

    const config = useConfig();
    const {
        chainId,
    } = config;

    const {
        address,
    } = useSelector(
        (state) => state.settings
    );

    const isLoading = useMemo(() => {
        if (page === '/farming') {
            return loading
            || _no(data.length)
            || _no(farmingContext.activeFarms.length);
        }
        return false;
    }, [page, loading, data.length, farmingContext.activeFarms.length]);

    const consolidatedData = useMemo(() => {
        return [
            ...data
        ];
    }, [data]);

    const averageAPY = useOpenFarmsAverageAPY({
        data: consolidatedData
    });

    const totalDepositedValue = useMemo(() => {
        if (page === '/farming') {
            return consolidatedData
                .map(currentFarm => Number(currentFarm.amountInUSD ?? 0))
                .filter(v => !isNaN(v) && isFinite(v))
                .reduce((totalAmount, v) => totalAmount + v, 0);
        } else if (page === '/RWAvaults') {
            return farmingContext.activeRWAs
                .map(currentFarm => Number(currentFarm.positionAmountInUSD ?? 0))
                .filter(v => !isNaN(v) && isFinite(v))
                .reduce((totalAmount, v) => totalAmount + v, 0);
        }
        return 0;
    }, [consolidatedData, farmingContext.activeRWAs, page]);

    const totalValuation = useMemo(() => {
        const farms = page === '/farming' ? farmingContext.activeFarms : [];
        const rwas = page === '/RWAvaults' ? farmingContext.activeRWAs : [];
        
        return [
            ...farms,
            ...rwas
        ]
        .map(currentFarm => Number(currentFarm.rewardsValue ?? 0))
        .filter(v => !isNaN(v) && isFinite(v))
        .reduce((totalAmount, v) => totalAmount + v, 0);
    }, [page, farmingContext.activeFarms, farmingContext.activeRWAs]);

    /*
    const totalAPY = useMemo(() => {
        return farmingContext.activeFarms
            .reduce((totalAmount, currentFarm) => {
                // console.log(currentFarm, 'currentFarm');
                return totalAmount += Number(currentFarm.rewardsValue ?? 0);
            }, 0);
    }, [farmingContext.activeFarms]);
    */

    const { totalClaimedData } = useRWAOverview({
        chainId,
        userAddress: address,
    });

    const items = useMemo(() => {

        const estimatedEarn = totalValuation
            - totalDepositedValue;

        return [
            {
                title: "Total Deposited Value",
                counter: displayWithSkeleton({
                    isLoading,
                    data: formatMoney(
                        totalDepositedValue,
                        true
                    ),
                    isEmpty
                }),
                icon: "wallet",
                color: "#CABDFF",
                tooltip: "Small description Available for withdrawal",
            },
            {
                title: "Current Valuation",
                counter: displayWithSkeleton({
                    isLoading,
                    data: page === '/RWAvaults' ? 
                        '$' + totalValuation.toFixed(6) :
                        formatMoney(
                            totalValuation,
                            true
                        ),
                    isEmpty
                }),
                icon: "activity",
                color: "#B5E4CA",
                tooltip: "Small description Current account balance",
            },
            page === "/farming" && {
                title: "Average APY",
                counter: displayWithSkeleton({
                    isLoading: averageAPY === null,
                    data: `${formatMoney(
                        averageAPY?.value,
                        false,
                        true
                    )}%`,
                    isEmpty
                }),
                icon: "promotion",
                color: averageAPY?.color || '#B5E4CA',
                tooltip: "Small description Available for withdrawal",
            },
            page === "/farming" && {
                title: "Estimated Earn",
                counter: displayWithSkeleton({
                    isLoading,
                    data: formatMoney(
                        estimatedEarn,
                        true
                    ),
                    isEmpty
                }),
                icon: "pie-chart",
                color: "#B1E5FC",
                tooltip: "Small description Available for withdrawal",
            },
            page === "/RWAvaults" && {
                title: "Total Claimed",
                counter: displayWithSkeleton({
                    isLoading,
                    data: formatMoney(
                        totalClaimedData,
                        true
                    ),
                    isEmpty
                }),
                icon: "pie-chart",
                color: "#B1E5FC",
                tooltip: "Small description Available for withdrawal",
            },
        ].filter(Boolean); // Remove null values
    }, [totalValuation, totalDepositedValue, isLoading, isEmpty, page, averageAPY, totalClaimedData]);

    return (
        <>
            <Card
                className={cn(styles.card, className)}
                // title="Overview"
                classTitle="title-green"
            >
                <div className={styles.overview}>
                    <div className={styles.list}>
                        {items.map((x, index) => (
                            <div className={styles.item} key={index}>
                                <div
                                    className={styles.icon}
                                    style={{ backgroundColor: x.color }}
                                >
                                    <Icon name={x.icon} size="24" />
                                </div>
                                <div className={styles.details}>
                                    <div className={styles.label}>
                                        {x.title}
                                        {/*<Tooltip
                                            className={styles.tooltip}
                                            title={x.tooltip}
                                            icon="info"
                                            place="top"
                                        />*/}
                                    </div>
                                    {
                                        !!x.counter && (
                                            <div className={styles.counter}>
                                                {x.title === "Current Valuation" && page === '/RWAvaults' ? (
                                                    <AnimatedNumbers
                                                        value={isNaN(Number(x.counter?.replace('$', '') || 0)) ? 0 : Number(x.counter?.replace('$', '') || 0)}
                                                        decimals={6}
                                                        prefix="$"
                                                    />
                                                ) : (
                                                    x.counter
                                                )}
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
            {/*<button
                className={cn("button", styles.button)}
                onClick={() => setVisibleModal(true)}
            >
                Withdraw balance
            </button>*/}
            <TooltipGlobal />
            <Modal
                outerClassName={styles.outer}
                visible={visibleModal}
                onClose={() => setVisibleModal(false)}
            >
                <Success />
            </Modal>
        </>
    );
};

export default Overview;
